import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FilterAlt,
  Close,
  ExpandMore as ExpandMoreIcon,
  SearchOutlined,
  PriceChangeOutlined,
  CheckBoxOutlined,
} from '@mui/icons-material';
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  TextField,
  Chip,
} from '@mui/material';

import { Parameter } from '../models/Parameter';
import { useConfig } from '../utils/useConfig';

type Props = {
  parameters: Parameter[];
  selectedFilters: { [key: string]: string[] };
  onFilterChange: (
    parameterId: string,
    value: string,
    isSelected: boolean,
  ) => void;
  onClearAllFilters: () => void;
  onApplyFilters: (filters: { [key: string]: string[] }) => void;
  minPrice: number;
  maxPrice: number;
  totalPriceRange: number[];
  onPriceRangeChange: (range: number[]) => void;
  isDesktop?: boolean;
};

function Filters({
  parameters,
  selectedFilters,
  onFilterChange,
  onClearAllFilters,
  onApplyFilters,
  minPrice,
  maxPrice,
  totalPriceRange,
  onPriceRangeChange,
  isDesktop = true,
}: Props) {
  const config = useConfig();
  const { colors } = config.theme;

  const primaryColor = colors.primary;
  const primaryLight = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.1)`;
  const primaryLightBorder = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.2)`;

  const [temporaryFilters, setTemporaryFilters] = useState<{
    [key: string]: string[];
  }>(selectedFilters);

  const [expandedParameters, setExpandedParameters] = useState<{
    [key: number]: boolean;
  }>({});

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [modifiedFilters, setModifiedFilters] = useState<boolean>(false);
  const [priceChanged, setPriceChanged] = useState<boolean>(false);
  const [selectedFilterCount, setSelectedFilterCount] = useState<number>(0);
  const [internalPriceRange, setInternalPriceRange] = useState<number[]>([
    minPrice,
    maxPrice,
  ]);

  useEffect(() => {
    setTemporaryFilters(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    let count = 0;
    Object.values(temporaryFilters).forEach((valueArray) => {
      count += valueArray.length;
    });

    setSelectedFilterCount(count);
  }, [temporaryFilters]);

  useEffect(() => {
    const filtersChanged =
      JSON.stringify(temporaryFilters) !== JSON.stringify(selectedFilters);
    const priceModified =
      internalPriceRange[0] !== minPrice || internalPriceRange[1] !== maxPrice;

    const newModifiedState = filtersChanged || priceModified || priceChanged;
    if (modifiedFilters !== newModifiedState) {
      setModifiedFilters(newModifiedState);
    }
  }, [
    temporaryFilters,
    selectedFilters,
    internalPriceRange,
    minPrice,
    maxPrice,
    priceChanged,
    modifiedFilters,
  ]);

  const handleCheckboxChange = (
    parameterId: string,
    value: string,
    isChecked: boolean,
  ) => {
    setTemporaryFilters((prev) => {
      const prevValues = prev[parameterId] || [];

      if (isChecked) {
        return {
          ...prev,
          [parameterId]: [...prevValues, value],
        };
      } else {
        const newValues = prevValues.filter((v) => v !== value);
        const newFilters = { ...prev };

        if (newValues.length === 0) {
          delete newFilters[parameterId];
        } else {
          newFilters[parameterId] = newValues;
        }

        return newFilters;
      }
    });
  };

  const handleClearAll = () => {
    setTemporaryFilters({});
    onApplyFilters({});
    setModifiedFilters(false);
    setPriceChanged(false);
  };

  const handleApply = () => {
    const changes: [string, string, boolean][] = [];

    Object.entries(temporaryFilters).forEach(([parameterId, values]) => {
      values.forEach((value) => {
        if (!selectedFilters[parameterId]?.includes(value)) {
          changes.push([parameterId, value, true]);
        }
      });
    });

    Object.entries(selectedFilters).forEach(([parameterId, values]) => {
      values.forEach((value) => {
        if (!temporaryFilters[parameterId]?.includes(value)) {
          changes.push([parameterId, value, false]);
        }
      });
    });

    if (changes.length > 0) {
      onApplyFilters(temporaryFilters);
    }

    setModifiedFilters(false);
  };

  const handleToggle = (parameterId: number) => {
    setExpandedParameters((prev) => ({
      ...prev,
      [parameterId]: !prev[parameterId],
    }));
  };

  useEffect(() => {
    const newExpandedState = { ...expandedParameters };
    let hasChanges = false;

    Object.entries(selectedFilters).forEach(([paramId, values]) => {
      const paramIdNum = Number(paramId);
      if (values.length > 0 && !expandedParameters[paramIdNum]) {
        newExpandedState[paramIdNum] = true;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setExpandedParameters(newExpandedState);
    }
  }, [selectedFilters]);

  const filteredParameters = parameters.filter((parameter) => {
    if (selectedFilters[parameter.id]?.length > 0) return true;

    if (!searchQuery) return parameter.parameterValues.length > 0;

    const queryLower = searchQuery.toLowerCase();

    if (parameter.name.toLowerCase().includes(queryLower)) return true;

    return parameter.parameterValues.some((val) =>
      val.value.toLowerCase().includes(queryLower),
    );
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.07 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  return (
    <motion.div
      className="space-y-5"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="p-4 space-y-5">
        <motion.div variants={itemVariants}>
          <TextField
            placeholder="Szukaj w filtrach..."
            variant="outlined"
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchOutlined
                  fontSize="small"
                  className="mr-2"
                  style={{ color: `${primaryColor}99` }}
                />
              ),
              className: 'bg-white',
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryLightBorder,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${primaryColor}80`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
              },
            }}
          />
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="p-3 rounded-lg border shadow-sm"
          style={{
            background: `linear-gradient(to bottom right, ${primaryLight}, white)`,
            borderColor: primaryLightBorder,
          }}
        >
          <div className="flex items-center mb-3">
            <PriceChangeOutlined
              fontSize="small"
              className="mr-2"
              style={{ color: colors.primary }}
            />
            <p className="font-medium text-gray-800">Zakres cenowy</p>
          </div>

          <div className="px-2"></div>

          <div className="flex justify-between mt-2 text-sm">
            <div
              className="bg-white px-2 py-1 rounded border min-w-[80px] text-center"
              style={{ borderColor: primaryLightBorder }}
            >
              {formatPrice(internalPriceRange[0])} zł
            </div>
            <div
              className="bg-white px-2 py-1 rounded border min-w-[80px] text-center"
              style={{ borderColor: primaryLightBorder }}
            >
              {formatPrice(internalPriceRange[1])} zł
            </div>
          </div>
        </motion.div>

        <div className="space-y-2">
          <AnimatePresence>
            {filteredParameters.length === 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-center p-3 bg-gray-50 rounded-lg"
              >
                <p className="text-gray-500 text-sm">
                  Brak parametrów pasujących do wyszukiwania
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {filteredParameters.map((parameter) => (
            <motion.div
              key={parameter.id}
              variants={itemVariants}
              className="rounded-lg border shadow-sm overflow-hidden"
              style={{
                background: `linear-gradient(to bottom right, ${primaryLight}, white)`,
                borderColor: primaryLightBorder,
              }}
            >
              <div
                className={`flex items-center hover:bg-opacity-50 transition-colors justify-between p-3 cursor-pointer transition-colors ${
                  expandedParameters[parameter.id] ? 'border-b' : ''
                }`}
                style={{
                  borderColor: expandedParameters[parameter.id]
                    ? primaryLightBorder
                    : 'transparent',
                }}
                onClick={() => handleToggle(parameter.id)}
              >
                <div className="flex items-center">
                  <CheckBoxOutlined
                    fontSize="small"
                    className="mr-2"
                    style={{ color: colors.primary }}
                  />
                  <p className="font-medium text-gray-800">{parameter.name}</p>
                  {selectedFilters[parameter.id]?.length > 0 && (
                    <Chip
                      label={selectedFilters[parameter.id].length}
                      size="small"
                      color="primary"
                      className="ml-2 h-5 min-w-5"
                      style={{ backgroundColor: colors.primary }}
                    />
                  )}
                </div>

                <IconButton size="small" className="focus:outline-none">
                  <ExpandMoreIcon
                    className="transition-transform duration-200"
                    style={{
                      transform: expandedParameters[parameter.id]
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    }}
                  />
                </IconButton>
              </div>

              <Collapse in={expandedParameters[parameter.id]}>
                <div className="p-2 max-h-60 overflow-y-auto bg-white bg-opacity-70">
                  {parameter.parameterValues
                    .filter(
                      (value) =>
                        !searchQuery ||
                        value.value
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                    )
                    .map((value) => (
                      <FormControlLabel
                        className="m-0 p-1 w-full hover:bg-gray-100 rounded-md transition-colors"
                        key={value.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              temporaryFilters[parameter.id]?.includes(
                                value.value,
                              ) || false
                            }
                            onChange={(e) =>
                              handleCheckboxChange(
                                parameter.id.toString(),
                                value.value,
                                e.target.checked,
                              )
                            }
                            className="p-1"
                          />
                        }
                        label={<span className="text-sm">{value.value}</span>}
                      />
                    ))}

                  {parameter.parameterValues.filter(
                    (value) =>
                      !searchQuery ||
                      value.value
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                  ).length === 0 && (
                    <p className="text-gray-500 text-xs text-center p-2">
                      Brak wartości pasujących do wyszukiwania
                    </p>
                  )}
                </div>
              </Collapse>
            </motion.div>
          ))}
        </div>

        <motion.div
          variants={itemVariants}
          className={`
                ${!isDesktop ? 'sticky bottom-0 z-10' : ''}
                bg-white py-3 px-2 border-t border-gray-200 mt-4
                shadow-[0_-2px_10px_rgba(0,0,0,0.05)]
              `}
        >
          <div className="flex items-center gap-2">
            <motion.button
              onClick={handleClearAll}
              disabled={Object.keys(temporaryFilters).length === 0}
              className={`
                    flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium
                    ${
                      Object.keys(temporaryFilters).length === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }
                    transition-colors
                  `}
              whileHover={
                Object.keys(temporaryFilters).length > 0 ? { scale: 1.02 } : {}
              }
              whileTap={
                Object.keys(temporaryFilters).length > 0 ? { scale: 0.98 } : {}
              }
            >
              <Close fontSize="small" className="mr-1.5" />
              Wyczyść filtry
              {selectedFilterCount > 0 && (
                <span className="ml-1.5 bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
                  {selectedFilterCount}
                </span>
              )}
            </motion.button>

            <motion.button
              onClick={handleApply}
              className="flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: colors.primary }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FilterAlt fontSize="small" className="mr-1.5" />
              Zastosuj filtry
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Filters;
